import { useContext, useEffect, useRef, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { Check } from "lucide-react"
import { renderMarkdown } from "../utils/markdown"
import { api, API_HOST, Feature, LlmQuotaError, parseCheckinViaJob, getCheckinsByIds, goToLoginIfSessionClosed } from "../api/client"
import { QuotaBanner } from "../components/QuotaBanner"
import { UserContext } from "../components/UserContext"
import ChatInterface from "../components/ChatInterface"
import ParseActivityPanel from "../components/checkin/ParseActivityPanel"
import CheckinSetView from "../components/CheckinSetView"
import BulkEditPreparedPanel from "../components/BulkEditPreparedPanel"
import { reconstructDataFromNormalized } from "../utils/dataNormalization"
import {
    GenieMessage,
    appendCheckinEvent,
    applyParsedCheckin,
    applyRefreshedCheckins,
} from "../utils/genieMessages"
import React from "react"
import {
    emptyParseActivity,
    useParseActivity,
    type ParseActivity,
    type ParseTracker,
} from "../hooks/useParseActivity"

/**
 * The key a Genie *turn*'s activity record lives under in `useParseActivity`.
 *
 * The hook is keyed by checkin id and this surface needs one more record that
 * belongs to no checkin — the turn itself. Negative, because checkin ids are
 * positive, so the two can never collide. Reusing the hook rather than keeping
 * a second record by hand is what gets the turn panel the sticky technical tier
 * and the expanded flag for free; the socket half of the hook is simply unused
 * here, since a turn's events arrive on the chat socket the page already owns.
 */
const GENIE_TURN_KEY = -1

/**
 * What the turn indicator draws before its turn has reported anything. Stepless,
 * so the panel renders the one line Genie's indicator has always shown.
 * Module-level so it keeps one identity across renders.
 */
const TURN_IDLE: ParseActivity = emptyParseActivity(0)

/**
 * Admin-only debug detail for a committed bulk edit.
 *
 * The verification re-run's remaining count used to ride along in the narrative
 * ("Applied 5 changes (1 checkin still matches the rule)"). It fires for benign
 * reasons — a body that was written exactly as asked but happens not to contain
 * the keywords the compiler picked still satisfies the selection afterwards —
 * and phrased in terms of "the rule" it reads as an alarm about a commit that
 * worked. It's an internal consistency check, so it lives here, next to the
 * dry run's skip notes.
 */
const BulkEditCommittedDebug: React.FC<{ data: any }> = ({ data }) => {
    const { user } = React.useContext(UserContext)
    const [showDebug, setShowDebug] = React.useState(false)

    if (!user?.isAdmin || !data) return null

    const verificationCount = data.verification_count
    const verified = typeof verificationCount === 'number' && verificationCount >= 0

    return (
        <div className="mt-1">
            <button
                type="button"
                onClick={() => setShowDebug(s => !s)}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
                {showDebug ? "Hide debug details" : "Show debug details (admin)"}
            </button>
            {showDebug && (
                <div className="sm:border-l-4 pl-3 sm:pl-4 py-2 mt-2 sm:border-dashed sm:border-gray-400 bg-gray-50 text-xs text-gray-600">
                    <div>
                        journal_id={data.journal_id ?? '—'} • applied={data.applied ?? 0}
                        {data.dropped ? ` • dropped=${data.dropped}` : ''}
                    </div>
                    <div>
                        {verified
                            ? `verification: ${verificationCount} candidate${verificationCount === 1 ? '' : 's'} still match${verificationCount === 1 ? 'es' : ''} the rule`
                            : 'verification: not run (enumerated or expensive selection)'}
                    </div>
                </div>
            )}
        </div>
    )
}

interface GenieChatState {
    chatId: string | null
    messages: GenieMessage[]
    isConnecting: boolean
    isTyping: boolean
    currentActivity: string | null  // Store current function reasoning for display
    error: string | null
    quotaError: LlmQuotaError | null
    result?: any  // Store the created project/feature data
}

const GenieChat = () => {
    const { chatId: chatIdFromUrl } = useParams<{ chatId: string }>()
    const navigate = useNavigate()
    const { user } = useContext(UserContext)

    // Helper function to provide user-friendly names for function calls
    const getFunctionDisplayName = (functionName: string): string => {
        const displayNames: Record<string, string> = {
            'draft_checkin_changes': 'Working out which checkins to change...',
            'revise_draft': 'Reworking the proposal...',
            'commit_draft': 'Applying the changes...',
            'discard_draft': 'Dropping that proposal...',
            'query_checkins': 'Loading your recent checkins...',
            'create_project': 'Creating new project...',
            'create_feature': 'Setting up new feature...',
            'search_personas': 'Finding relevant advisors...',
            'undo': 'Reversing the changes...',
            // Kept: older chats replay `ass:__control` rows naming tools the
            // model is no longer offered, and they should still read as English.
            'undo_bulk_edit': 'Reversing the changes...',
            'update_project_draft': 'Updating project details...',
            'forget_bulk_edit_journal': 'Discarding the undo history...',
        }
        return displayNames[functionName] || `Working on ${functionName}...`
    }
    
    const [chatState, setChatState] = useState<GenieChatState>({
        chatId: chatIdFromUrl || null,
        messages: [],
        isConnecting: false,
        isTyping: false,
        currentActivity: null,
        error: null,
        quotaError: null
    })

    const [features, setFeatures] = useState<Feature[]>([])
    const [analyzingCheckinIds, setAnalyzingCheckinIds] = useState<Set<number>>(new Set())
    // One hook for both live views on this page: the record for each check-in a
    // turn creates and parses (as a watched job, exactly as the composer
    // does), and the record for the turn itself under `GENIE_TURN_KEY`.
    const {
        activities: parseActivities,
        beginParse,
        setExpanded: setParseExpanded,
    } = useParseActivity()
    // `beginParse` is stable, but the callbacks below are re-created per render
    // and the ref keeps that from being something a reader has to verify.
    const beginParseRef = useRef(beginParse)
    beginParseRef.current = beginParse
    // The turn currently streaming into `GENIE_TURN_KEY`. Claimed when the
    // indicator goes up, which is the one signal every turn raises — a typed
    // message, an approval, a bulk-edit confirmation.
    const turnTrackerRef = useRef<ParseTracker | null>(null)
    const turnActivity = parseActivities[GENIE_TURN_KEY] ?? TURN_IDLE
    
    // Load features when any message needs them: rendered checkins, or
    // bulk-edit prepared panels (which display feature pills inline).
    useEffect(() => {
        const needsFeatures = chatState.messages.some(
            msg => msg.role === "checkin" || msg.role === "bulk_edit_prepared" || msg.role === "selected_checkin_list"
        )

        if (needsFeatures && user && features.length === 0) {
            const loadFeatures = async () => {
                try {
                    const response = await api.get("/api/features/all")
                    setFeatures(response.data)
                } catch (error) {
                    console.error("Failed to load features:", error)
                }
            }
            loadFeatures()
        }
    }, [chatState.messages, user, features.length])
    
    // Debug: Monitor state changes
    useEffect(() => {
        console.log("ChatState updated:", {
            messages: chatState.messages.length,
            isTyping: chatState.isTyping,
            chatId: chatState.chatId,
            messages_detail: chatState.messages
        })
    }, [chatState])
    const socket = useRef<WebSocket | null>(null)
    const isConnecting = useRef(false)
    const isLoadingHistory = useRef(false)

    // Use this to dedupe chunks
    const messageChunks = useRef<Map<number, Set<number>>>(new Map())
    
    // Whether this turn has already claimed its record. Claiming twice would
    // reset a turn's steps mid-flight, which is the whole reason this is a flag
    // rather than "claim wherever it is convenient".
    const turnClaimedRef = useRef(false)

    /**
     * Claim the record for the turn in progress, once.
     *
     * A turn starts when the indicator goes up, and that is the one signal every
     * kind of turn raises — a typed message, an approval, a bulk-edit
     * confirmation — so no send site has to remember to do anything.
     */
    const claimTurn = () => {
        if (turnClaimedRef.current) return
        turnClaimedRef.current = true
        turnTrackerRef.current = beginParseRef.current(GENIE_TURN_KEY)
    }

    useEffect(() => {
        if (chatState.isTyping) claimTurn()
        // The indicator came down: the turn is over, and the next one claims a
        // fresh record rather than growing this one.
        else turnClaimedRef.current = false
    }, [chatState.isTyping])

    /**
     * One activity event off the chat socket, folded into this turn's record.
     *
     * Claims first, because the server can send its first event before React has
     * flushed the state change that raised the indicator — and an event dropped
     * there is a step missing from the panel for the rest of the turn.
     * `claimTurn` being idempotent is what stops the effect above from then
     * resetting what this just folded.
     */
    const trackTurnActivity = (event: any) => {
        if (!event || typeof event !== "object") return
        claimTurn()
        turnTrackerRef.current?.onActivity(event)
    }

    const parseCheckinAfterCreation = async (checkinId: number, checkinBody: string, genieMessageId: number) => {
        // Claimed here, at commit-to-parse, and not beside the indicator: a
        // tracker claimed early supersedes whatever tracker holds the watch of
        // a parse still in flight, and closing that watch leaves the parse
        // running with nothing here hearing how it went. This path commits
        // immediately, so the two are one line apart — but the rule is the
        // rule.
        const tracker = beginParseRef.current(checkinId)
        try {
            // Set analyzing state
            setAnalyzingCheckinIds(prev => new Set(prev).add(checkinId))

            const parseResponse = await parseCheckinViaJob(checkinId, checkinBody, 'composer', {
                // Stage A complete - the chips wait for the full parse here,
                // but the check-in card's own panel gets Stage A's flavor
                // words: it is watching a real parse of a real note, so it
                // should read the way the composer's does. Genie's *turn*
                // panel is the one that stays on its mechanism line —
                // a turn runs no Stage A and has no note to characterize.
                onStageAComplete: (_detectedProjectIds, statusWords) =>
                    tracker.onStatusWords(statusWords),
                genieMessageId,
                // The panel's feed, and the handle the hook closes the watch
                // through when this page unmounts mid-parse.
                onActivity: tracker.onActivity,
                onSocket: tracker.onSocket,
            })

            // Update only the message that triggered this parse, so earlier
            // create/update snapshots for the same checkin keep their content.
            setChatState(prev => ({
                ...prev,
                messages: applyParsedCheckin(
                    prev.messages,
                    genieMessageId,
                    parseResponse.checkin,
                    parseResponse.collection_items || {},
                )
            }))

            // Small delay to ensure user sees the analyzing state
            await new Promise(resolve => setTimeout(resolve, 300))

        } catch (error) {
            console.error("Failed to parse checkin after creation:", error)
            // Don't fail the UI - just log the error
        } finally {
            // Closes this parse's socket and keeps its record, whichever way
            // the call ended.
            tracker.end()
            // Clear analyzing state
            setAnalyzingCheckinIds(prev => {
                const newSet = new Set(prev)
                newSet.delete(checkinId)
                return newSet
            })
        }
    }

    useEffect(() => {
        // Connect to existing chat if we have a chat ID
        if (chatState.chatId) {
            console.log("Connecting to chat:", chatState.chatId)
            connectWebSocket(chatState.chatId)
        }
        // Don't automatically start a new chat - wait for user's first message
        
        return () => {
            if (socket.current && socket.current.readyState === WebSocket.OPEN) {
                socket.current.close()
            }
        }
    }, [chatState.chatId])



    const connectWebSocket = (chatId: string) => {
        console.log("Connecting WebSocket for chat:", chatId)
        
        // Prevent double connections in StrictMode
        if (isConnecting.current) {
            console.log("Already connecting, skipping...")
            return
        }
        
        // Close existing connection if any
        if (socket.current && socket.current.readyState === WebSocket.OPEN) {
            console.log("Closing existing WebSocket")
            socket.current.close()
        }

        isConnecting.current = true

        // Create WebSocket connection
        const protocol = window.location.protocol === "https:" ? "wss" : "ws"
        const wsUrl = `${protocol}://${API_HOST}/api/genie/ws/${chatId}`
        console.log("WebSocket URL:", wsUrl)

        const ws = new WebSocket(wsUrl)
        socket.current = ws

        ws.onopen = () => {
            console.log("WebSocket opened")
            isConnecting.current = false
            isLoadingHistory.current = true  // Set flag for initial message load
            setChatState(prev => ({ ...prev, isConnecting: false }))
        }

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data)
            console.log("WebSocket message received:", data)

            if (data.type === "typing") {
                // Show typing indicator
                setChatState(prev => ({
                    ...prev,
                    isTyping: true
                }))
            } else if (data.type === "activity") {
                // One parse-activity event for the turn in progress. The only
                // message type this surface gained for the live view — the
                // sink, the fold and the panel are all the composer's.
                trackTurnActivity(data.event)
            } else if (data.type === "message") {
                // Filter out function call messages that contain JSON with function_name
                let isFunction = false
                if (data.role === "assistant") {
                    try {
                        const parsed = JSON.parse(data.content)
                        if (parsed.function_name && parsed.reasoning) {
                            // This is a function call message, convert it to activity status
                            const activity = parsed.reasoning || getFunctionDisplayName(parsed.function_name)
                            setChatState(prev => ({
                                ...prev,
                                isTyping: true,
                                currentActivity: activity
                            }))
                            isFunction = true
                        }
                    } catch (e) {
                        // Not JSON, treat as regular message
                    }
                }

                if (!isFunction) {
                    // Add or update regular message
                    console.log("Adding message to state:", {
                        role: data.role,
                        content: data.content,
                        currentMessages: chatState.messages.length
                    })
                    setChatState(prev => {
                        console.log("Previous messages:", prev.messages)
                        const newMessages = [...prev.messages, {
                            role: data.role,
                            content: data.content,
                            renderedContent: renderMarkdown(data.content)
                        }]
                        console.log("New messages:", newMessages)
                        return {
                            ...prev,
                            messages: newMessages,
                            isTyping: false,
                            currentActivity: null
                        }
                    })
                }
            } else if (data.type === "chunk") {
                // Handle streaming chunks
                setChatState(prev => {
                    const updatedMessages = [...prev.messages]
                    const lastMessage = updatedMessages[updatedMessages.length - 1]
                    
                    if (lastMessage && lastMessage.id === data.message_id) {
                        if (!messageChunks.current.has(data.message_id)) {
                            messageChunks.current.set(data.message_id, new Set())
                        }
                        if (messageChunks.current.get(data.message_id)?.has(data.chunk_index)) {
                            return prev
                        }
                        messageChunks.current.get(data.message_id)?.add(data.chunk_index)
                        
                        // Parse the entire accumulated content with each new chunk
                        const rawContent = lastMessage.content + data.content
                        lastMessage.content = rawContent
                        lastMessage.renderedContent = renderMarkdown(rawContent)
                    } else {
                        updatedMessages.push({
                            id: data.message_id,
                            role: "assistant",
                            content: data.content,
                            renderedContent: renderMarkdown(data.content),
                            pending: true
                        })
                    }
                    
                    return { ...prev, messages: updatedMessages, isTyping: false, currentActivity: null }
                })
            } else if (data.type === "complete") {
                // Handle message completion
                setChatState(prev => {
                    const updatedMessages = prev.messages.map(msg =>
                        msg.id === data.message_id
                            ? { 
                                ...msg, 
                                content: data.content, 
                                renderedContent: renderMarkdown(data.content),
                                pending: false
                              }
                            : msg
                    )
                    
                    return {
                        ...prev,
                        messages: updatedMessages,
                        isTyping: false,
                        currentActivity: null
                    }
                })
            } else if (data.type === "title_update") {
                // Handle title update (optional - could update page title or header)
                console.log("Chat title updated:", data.title)
            } else if (data.type === "function_result") {
                // Handle function call results
                if (data.function === "create_project" && data.success) {
                    setChatState(prev => ({ 
                        ...prev, 
                        result: data.data 
                    }))
                    
                    // Redirect to the created project after a short delay
                    setTimeout(() => {
                        navigate(`/projects/${data.data.project_id}`)
                    }, 2000)
                } else if (data.function === "create_checkin" && data.success) {
                    // Checkin created successfully - will be displayed by checkin_created message
                }
            } else if (data.type === "checkin_created" || data.type === "checkin_updated") {
                // One event per write while a batch commits, one event carrying
                // the whole batch on reload — both fold into a single message
                // per journal, which is what the stored row holds.
                const isUpdated = data.type === "checkin_updated"
                const isFresh = !isLoadingHistory.current
                const { checkins } = reconstructDataFromNormalized(data)

                // An update whose checkin is gone has nothing to show; a create
                // still renders its "created here, but later deleted" note.
                if (isUpdated && checkins.length === 0) {
                    console.warn("checkin_updated arrived with no checkin data", data.checkin_ids)
                    return
                }

                setChatState(prev => ({
                    ...prev,
                    messages: appendCheckinEvent(prev.messages, data, {
                        isUpdated,
                        freshlyMutatedInSession: isFresh,
                    }),
                    isTyping: false,
                    currentActivity: null
                }))

                // Kick off the parse for anything that arrived unparsed — a create
                // lands with empty features until the parser runs, and an update
                // needs its snapshot re-derived. Skipped on reload.
                if (!isFresh) return
                for (const checkin of checkins) {
                    const hasExistingParsedData = (
                        (checkin.checkin_features && checkin.checkin_features.length > 0) ||
                        (checkin.checkin_projects && checkin.checkin_projects.length > 0)
                    )
                    if (isUpdated || !hasExistingParsedData) {
                        parseCheckinAfterCreation(checkin.id, checkin.body, data.genie_message_id)
                    }
                }
            } else if (data.type === "assistant_function") {
                // Use function call reasoning to set current activity for typing indicator
                const activity = data.reasoning || getFunctionDisplayName(data.function_name)
                setChatState(prev => ({
                    ...prev,
                    isTyping: true,
                    currentActivity: activity
                }))
            } else if (data.type === "list_checkins") {
                // Add a list checkin message for review
                setChatState(prev => ({
                    ...prev,
                    messages: [...prev.messages, {
                        role: "list_checkins",
                        content: "",
                        data: data.data,
                        action: data.action
                    }],
                    isTyping: false,
                    currentActivity: null
                }))
            } else if (data.type === "selected_checkin_list") {
                // Seeded message from "Discuss" deeplink — surface the
                // referenced checkins so the user has them in view before
                // typing their actual question.
                setChatState(prev => ({
                    ...prev,
                    messages: [...prev.messages, {
                        role: "selected_checkin_list",
                        content: "",
                        data: data.data,
                    }],
                    isTyping: false,
                    currentActivity: null
                }))
            } else if (data.type === "user_confirmation") {
                // Handle user confirmation messages
                if (data.message_type === "usr:confirm_bulk_edit") {
                    setChatState(prev => ({
                        ...prev,
                        messages: [...prev.messages, {
                            role: "narrative",
                            content: "",
                            messageType: data.message_type,
                            data: data.data
                        }]
                        // Keep isTyping and currentActivity as-is, don't clear until work is done
                    }))
                }
            } else if (data.type === "bulk_edit_prepared") {
                setChatState(prev => ({
                    ...prev,
                    messages: [...prev.messages, {
                        role: "bulk_edit_prepared",
                        content: "",
                        data: data
                    }],
                    isTyping: false,
                    currentActivity: null
                }))
            } else if (data.type === "bulk_edit_committed") {
                setChatState(prev => ({
                    ...prev,
                    messages: [...prev.messages, {
                        role: "narrative",
                        content: "",
                        messageType: "sys:bulk_edit_committed",
                        data: data
                    }],
                    isTyping: false,
                    currentActivity: null
                }))
            } else if (data.type === "bulk_edit_undone") {
                setChatState(prev => ({
                    ...prev,
                    messages: [...prev.messages, {
                        role: "narrative",
                        content: "",
                        messageType: "sys:bulk_edit_undone",
                        data: data
                    }],
                    isTyping: false,
                    currentActivity: null
                }))
            } else if (data.type === "bulk_edit_journal_forgotten") {
                setChatState(prev => ({
                    ...prev,
                    messages: [...prev.messages, {
                        role: "narrative",
                        content: "",
                        messageType: "sys:bulk_edit_journal_forgotten",
                        data: data
                    }],
                    isTyping: false,
                    currentActivity: null
                }))
            } else if (data.type === "complete_conversation") {
                // End conversation and clear typing indicator
                setChatState(prev => ({
                    ...prev,
                    isTyping: false,
                    currentActivity: null
                }))
            } else if (data.type === "quota_error") {
                // Distinct from `error` so the chat shows the structured banner
                // (with upgrade CTA on daily caps) rather than the misleading
                // "I had trouble understanding the request" toast.
                setChatState(prev => ({
                    ...prev,
                    quotaError: {
                        scope: data.scope,
                        used: data.used,
                        limit: data.limit,
                        window_seconds: data.window_seconds,
                        message: data.message
                    },
                    isTyping: false,
                    currentActivity: null
                }))
            } else if (data.type === "error") {
                setChatState(prev => ({
                    ...prev,
                    error: data.content,
                    isTyping: false,
                    currentActivity: null
                }))
            }
        }

        ws.onerror = (error) => {
            console.error("WebSocket error:", error)
            isConnecting.current = false
            setChatState(prev => ({ 
                ...prev, 
                error: "Connection error",
                isTyping: false,
                currentActivity: null
            }))
        }

        ws.onclose = (event) => {
            console.log("WebSocket closed", { code: event.code, reason: event.reason })
            isConnecting.current = false
            goToLoginIfSessionClosed(event)
        }
    }

    const sendMessage = async (message: string) => {
        if (!message.trim() || chatState.isConnecting || chatState.isTyping) {
            return
        }

        // If we don't have a chat yet, create one first
        if (!chatState.chatId) {
            try {
                setChatState(prev => ({ ...prev, isConnecting: true }))
                
                // Create a new Genie chat
                const response = await api.post("/api/genie/chats", {})
                
                const chatId = response.data.id
                setChatState(prev => ({ 
                    ...prev, 
                    chatId: chatId.toString(),
                    isConnecting: false 
                }))
                
                // Update URL
                navigate(`/genie/${chatId}`)
                
                // Wait for WebSocket to connect
                await new Promise((resolve) => {
                    const checkConnection = setInterval(() => {
                        if (socket.current && socket.current.readyState === WebSocket.OPEN) {
                            clearInterval(checkConnection)
                            resolve(true)
                        }
                    }, 100)
                })
            } catch (error) {
                console.error("Failed to start chat:", error)
                setChatState(prev => ({ 
                    ...prev, 
                    error: "Failed to start chat",
                    isConnecting: false 
                }))
                return
            }
        }

        // Make sure WebSocket is connected
        if (!socket.current || socket.current.readyState !== WebSocket.OPEN) {
            return
        }

        // Add user message to UI
        setChatState(prev => ({
            ...prev,
            messages: [...prev.messages, {
                role: "user",
                content: message,
                renderedContent: renderMarkdown(message)
            }]
        }))

        // Send message through WebSocket
        isLoadingHistory.current = false  // Clear history loading flag when user sends new message
        socket.current.send(JSON.stringify({
            type: "message",
            content: message,
            user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }))
    }
    
    const formatDate = (dateString: string, includeYearIfDifferent: boolean = false) => {
        // Guard against undefined or null dateString
        if (!dateString) {
            return 'Invalid date'
        }
        // Parse the date string directly as YYYY-MM-DD to avoid timezone issues
        const [year, month, day] = dateString.split('-').map(Number)
        const date = new Date(year, month - 1, day) // month is 0-indexed
        
        const currentYear = new Date().getFullYear()
        const shouldIncludeYear = includeYearIfDifferent && year !== currentYear
        
        // Use toLocaleDateString with options for a more user-friendly format
        // This will automatically use the user's browser locale settings
        return date.toLocaleDateString(undefined, { 
            weekday: 'short',
            month: 'short', 
            day: 'numeric',
            ...(shouldIncludeYear && { year: 'numeric' })
        })
    }
    
    // Helper function to render narrative messages with standardized styling
    const renderNarrativeMessage = (messageType: string, data: any, index: number) => {
        const getNarrativeText = (type: string, data: any) => {
            switch (type) {
                case "sys:create_checkins": {
                    const created = data?.count ?? 1
                    return created === 1 ? "Checkin created." : `${created} checkins created.`
                }
                case "sys:update_checkin": {
                    const updated = data?.count ?? 1
                    return updated === 1 ? "Checkin updated." : `${updated} checkins updated.`
                }
                case "usr:confirm_bulk_edit": {
                    // A partial approval says so — otherwise the transcript reads
                    // as if the whole proposal had been waved through.
                    const approved = data?.item_keys?.length
                    if (approved) {
                        return approved === 1
                            ? "You approved 1 of the changes."
                            : `You approved ${approved} of the changes.`
                    }
                    return "You approved the changes."
                }
                case "sys:bulk_edit_committed": {
                    // The verification re-run's "N still matches the rule" lives
                    // in the admin debug block below, not here: it fires for
                    // benign reasons (a written body that happens not to contain
                    // the keywords the compiler picked) and reads as an alarm.
                    const applied = data?.applied ?? 0
                    if (applied === 0) {
                        return data?.note || "Nothing to apply."
                    }
                    return applied === 1
                        ? "Applied 1 change."
                        : `Applied ${applied} changes.`
                }
                case "sys:bulk_edit_undone":
                    return "Changes reversed."
                case "sys:bulk_edit_journal_forgotten":
                    return data?.forgotten
                        ? "Undo history discarded — those changes can no longer be reversed."
                        : "That undo history was already gone."
                case "list_checkins_found":
                    const foundCount = data?.count || 0
                    const args = data?.args || {}

                    // Build filter description
                    const filters = []
                    if (args.text_search) filters.push(`most relevant to "${args.text_search}" or containing that term`)
                    if (args.feature_id) filters.push(`for feature ${args.feature_id}`)
                    if (args.project_id) filters.push(`for project ${args.project_id}`)
                    if (args.collection_item_id) filters.push(`for collection item ${args.collection_item_id}`)
                    if (args.start_date || args.end_date) {
                        if (args.start_date && args.end_date) {
                            filters.push(`from ${args.start_date} to ${args.end_date}`)
                        } else if (args.start_date) {
                            filters.push(`from ${args.start_date}`)
                        } else if (args.end_date) {
                            filters.push(`until ${args.end_date}`)
                        }
                    }
                    if (args.days_back) filters.push(`from last ${args.days_back} days`)

                    const filterText = filters.length > 0 ? ` ${filters.join(', ')}` : ''
                    return args.text_search
                        ? `Here's the ${foundCount} checkin${foundCount === 1 ? '' : 's'}${filterText}:`
                        : `Found ${foundCount} checkin${foundCount === 1 ? '' : 's'}${filterText}.`
                default:
                    return `System: ${type}`
            }
        }

        return (
            <div key={index} className="mb-4">
                <div className="text-gray-600 italic">
                    {getNarrativeText(messageType, data)}
                </div>
                {messageType === "sys:bulk_edit_committed" && <BulkEditCommittedDebug data={data} />}
            </div>
        )
    }

    const renderGenieMessage = (message: GenieMessage, index: number) => {
        // Handle narrative messages (system actions, user confirmations, etc.)
        if (message.role === "narrative") {
            return renderNarrativeMessage(message.messageType || "", message.data, index)
        }

        if (message.role === "checkin") {
            // One message per batch: the cards it holds are what the commit
            // wrote, capped by CheckinSetView so a 100-write approval doesn't
            // bury the conversation.
            const setCheckins = message.data?.checkins || []
            const writtenIds = message.checkinIds || []

            if (setCheckins.length === 0) {
                const verb = message.isUpdated ? 'updated' : 'created'
                return (
                    <div key={index} className="mb-4">
                        <div className="text-gray-600 italic">
                            {writtenIds.length > 1
                                ? `${writtenIds.length} checkins ${verb} here, but later deleted.`
                                : `Checkin ${verb} here, but later deleted.`}
                        </div>
                    </div>
                )
            }

            return (
                <CheckinSetView
                    key={index}
                    data={message.data}
                    features={features}
                    formatDate={formatDate}
                    lead={renderNarrativeMessage(
                        message.isUpdated ? "sys:update_checkin" : "sys:create_checkins",
                        { count: setCheckins.length },
                        -1,
                    )}
                    initialLimit={5}
                    suppressToggle={message.freshlyMutatedInSession}
                    analyzingCheckinIds={analyzingCheckinIds}
                    activities={parseActivities}
                    onToggleParseExpanded={setParseExpanded}
                    onRefresh={async () => {
                        try {
                            const featuresResponse = await api.get("/api/features/all")
                            setFeatures(featuresResponse.data)

                            if (writtenIds.length > 0) {
                                const checkinResponse = await getCheckinsByIds(writtenIds)
                                setChatState(prev => ({
                                    ...prev,
                                    messages: applyRefreshedCheckins(prev.messages, checkinResponse)
                                }))
                            }
                        } catch (error) {
                            console.error("Failed to refresh data:", error)
                        }
                    }}
                />
            )
        }

        if (message.role === "list_checkins") {
            const isReviewAction = message.action === "review"
            const isDisplayAction = message.action === "display"

            if (isReviewAction || isDisplayAction) {
                const checkinCount = (message.data?.checkins || []).length
                return (
                    <CheckinSetView
                        key={index}
                        data={message.data}
                        features={features}
                        formatDate={formatDate}
                        lead={renderNarrativeMessage("list_checkins_found", { count: checkinCount, args: message.data?.args }, index)}
                        initialLimit={5}
                    />
                )
            }
            
            return null
        }

        if (message.role === "selected_checkin_list") {
            const lead = (
                <div className="text-gray-600 italic mb-3">
                    You referenced:
                </div>
            )
            return (
                <CheckinSetView
                    key={index}
                    data={message.data}
                    features={features}
                    formatDate={formatDate}
                    lead={lead}
                />
            )
        }

        if (message.role === "bulk_edit_prepared") {
            // The proposal the user is being asked about is the newest one, not
            // whichever panel happens to sit last in the transcript. Genie can
            // speak after a proposal without replacing it — a correction that is
            // already satisfied gets a plain reply and the panel stands — and
            // gating on position took the Approve button away exactly then.
            // A revision supersedes this panel by rendering a newer one; a
            // commit retires it through `committed` below.
            const isLatestProposal = !chatState.messages
                .slice(index + 1)
                .some(m => m.role === "bulk_edit_prepared")
            const proposalId = message.data?.proposal_id
            const draftId = message.data?.draft_id
            // Has the corresponding commit narrative landed yet? Used to drop
            // the transient "Applying…" placeholder once the real result text is
            // up. Matched on either id: the panel's Approve commits under the
            // proposal_id it was rendered with, while the user saying "yes, do
            // it" goes through commit_draft, which knows the draft and not which
            // of its panels the user was looking at.
            const committed = chatState.messages.slice(index + 1).some(
                m => m.role === "narrative"
                    && m.messageType === "sys:bulk_edit_committed"
                    && (m.data?.proposal_id === proposalId
                        || (draftId != null && m.data?.draft_id === draftId))
            )
            return (
                <BulkEditPreparedPanel
                    key={index}
                    message={message}
                    socket={socket}
                    onApplying={() => setChatState(prev => ({
                        ...prev,
                        isTyping: true,
                        currentActivity: "Applying the changes...",
                    }))}
                    isLatestProposal={isLatestProposal}
                    formatDate={formatDate}
                    committed={committed}
                    onSendMessage={sendMessage}
                />
            )
        }

        return null
    }

    return (
        // Full height below the nav (h-16) and the shell's `py-4`, so the chat
        // column can stretch and pin the composer to the bottom of the screen.
        <div className="container mx-auto px-0 relative flex flex-col min-h-[calc(100dvh-6rem)] shell-fill-height">
            <div className="flex justify-center sm:mt-4 mb-6">
                <div className="w-full lg:w-2/3 xl:w-1/2 px-4">
                    <div className="flex justify-between items-center mb-4">
                        <h1 className="text-3xl font-bold text-gray-800">
                            Genie
                        </h1>
                        <Link 
                            to="/genie/history"
                            className="text-amber-800 hover:text-amber-900 text-sm font-medium"
                        >
                            View History →
                        </Link>
                    </div>
                    
                    <p className="text-gray-600 mb-4">
                        How can I help you today?
                    </p>
                </div>
            </div>

            {/* Chat Section */}
            <div className="flex flex-1 justify-center mt-4">
                <div className="w-full lg:w-2/3 xl:w-1/2 px-0 flex flex-col">
                    <ChatInterface
                        className="flex-1"
                        messages={chatState.messages as any}
                        onSendMessage={sendMessage}
                        isLoading={chatState.isTyping || chatState.isConnecting}
                        disabled={chatState.isConnecting || chatState.isTyping}
                        enableVoiceInput={true}
                        renderMessage={renderGenieMessage as any}
                        typingIndicator={
                            chatState.isTyping && (
                                <div className="mb-4">
                                    {/* The same panel the composer's "Analyzing"
                                        indicator expands into, fed by this
                                        turn's own events. `idleLabel` keeps the
                                        line Genie has always shown for a turn
                                        that never reaches a tool loop. */}
                                    <ParseActivityPanel
                                        activity={turnActivity}
                                        idleLabel={chatState.currentActivity || "Thinking"}
                                        onToggleExpanded={(expanded) =>
                                            setParseExpanded(GENIE_TURN_KEY, expanded)
                                        }
                                    />
                                </div>
                            )
                        }
                        footerContent={
                            <>
                                {chatState.quotaError && (
                                    <QuotaBanner
                                        quotaError={chatState.quotaError}
                                        onDismiss={() => setChatState(prev => ({ ...prev, quotaError: null }))}
                                    />
                                )}
                                {chatState.error && (
                                    <div className="text-rose-500 text-sm mb-2">{chatState.error}</div>
                                )}
                                {chatState.result && (
                                    <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-green-700 mb-2">
                                        <div className="flex items-center gap-2">
                                            <Check className="w-5 h-5" />
                                            <span>Project created successfully! Redirecting...</span>
                                        </div>
                                    </div>
                                )}
                            </>
                        }
                    />
                </div>
            </div>
        </div>
    )
}

export default GenieChat